import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import React, { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { pushError, pushNotification } from "../../../stores/hooks/useToasts";
import {
  PrimaryButton,
  PrimaryOutlinedButton,
} from "../../../ui/Button/Button";

export type StripeKeys = { clientSecret: string; publishableKey: string };

export const StripePaymentForm: FC<{
  customerEmail?: string;
  onCancel: VoidFunction;
  onComplete: () => Promise<void>;
}> = ({ onCancel, onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const result = await stripe.confirmPayment({
        confirmParams: {},
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        const error = new Error(
          result.error.message || t("buyNowModal.paymentFailed")
        );
        pushError(error);
      } else {
        pushNotification({
          message: t("buyNowModal.paymentSuccess"),
        });
        await onComplete();
      }
    } catch (err) {
      const error = new Error((err as Error)?.message || "Unexpected error");
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="grid gap-4" onSubmit={handleSubmit}>
      <PaymentElement />
      <PrimaryButton
        aria-label="Submit payment"
        busy={loading}
        className="h-12"
        disabled={!stripe || !elements}
        type="submit"
      >
        {t("buyNowModal.submit")}
      </PrimaryButton>
      <PrimaryOutlinedButton
        aria-label="Cancel payment"
        onClick={onCancel}
        type="reset"
      >
        {t("buyNowModal.cancel")}
      </PrimaryOutlinedButton>
    </form>
  );
};
